import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fehlenderAenderungstext } from "./reviewRules";
import { doc, getDoc } from "firebase/firestore";
import { db, app } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { SHOP } from "../shop";

const functions = getFunctions(app, "europe-west1");
const submitReviewFeedback = httpsCallable(functions, "submitReviewFeedback");

// ── Default German text (used if no Firestore config exists) ──────────────────

export const REVIEW_TEXT_DEFAULTS = {
  pageTitle: "DEIN ENTWURF",
  approveMainText:
    "Ich bestätige, dass ich den Entwurf überprüft und alle Details " +
    "(Namen, Daten, Texte, Farben) kontrolliert habe. Der Entwurf wird " +
    "genauso gedruckt, wie er hier dargestellt ist.",
  approveBullets: [
    "Der Entwurf sollte alle gewünschten grafischen Elemente, Texte und " +
      "Sprüche enthalten, da er exakt so produziert wird.",
    "Farben können auf dem Bildschirm vom Druckergebnis abweichen. Wenn du " +
      "unsicher bist, melde dich gerne vor der Freigabe bei uns.",
    "Die Freigabe ist verbindlich.",
    "Personalisierte Produkte sind vom Umtausch ausgeschlossen.",
  ],
  modificationLabel: "Ich wünsche weitere Anpassungen",
  modificationSubtext:
    "Der erste Entwurf und eine Überarbeitung sind im Angebot enthalten. " +
    "Weitere Änderungen können für 3,00 € pro Anpassung angefragt werden.",
  feedbackLabel: "Anpassungen:",
  feedbackPlaceholder: "Bitte hier deine Änderungswünsche eintragen...",
  // Erscheint, wenn „weitere Anpassungen" gewählt ist und das Feld leer
  // bleibt. Ohne Text wissen wir nicht, was zu ändern ist, und die Kundin
  // wartet auf eine Antwort, die niemand geben kann.
  feedbackRequiredHint:
    "Bitte beschreibe kurz, was angepasst werden soll — sonst wissen wir " +
    "nicht, worum es geht. Passt der Entwurf? Dann wähle oben die Freigabe.",
  submitButtonText: "Senden",
};

// ── Colour tokens ─────────────────────────────────────────────────────────────

const C = {
  pageBg:       SHOP.pageBg,
  card:         "#ffffff",
  primaryDark:  SHOP.primary,
  secondaryText:"#8a6f6a",
  border:       SHOP.border,
  selectedBg:   SHOP.pageBg,
  selectedBorder:SHOP.accentSoft,
  bulletText:   "#7a5c56",
};

// ── Component ─────────────────────────────────────────────────────────────────

const DraftReviewForm = ({
  previewMode = false,
  sampleData  = {},
  textConfig  = null,
}) => {
  const { draftId } = useParams();
  const navigate = useNavigate();

  const [images, setImages]                   = useState([]); // [{imageUrl, itemTitle, personalization}]
  const [reviewDecision, setReviewDecision]   = useState("approved");
  const [feedback, setFeedback]               = useState("");
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [customerEmail, setCustomerEmail]     = useState("");
  const [draftExists, setDraftExists]         = useState(true);
  const [texts, setTexts]                     = useState(REVIEW_TEXT_DEFAULTS);
  // Hinweis aus dem Auto-Entwurf (z. B. gewechselte Schrift), steht ueber dem Bild.
  const [notice, setNotice]                   = useState("");
  const [zoomed, setZoomed]                   = useState(null); // null | index
  const [isAdminView, setIsAdminView]         = useState(false);
  const [adminDecision, setAdminDecision]     = useState(null);
  const [adminFeedback, setAdminFeedback]     = useState(null);
  const [adminReviewedAt, setAdminReviewedAt] = useState(null);

  useEffect(() => {
    if (previewMode) {
      // Preview mode: use images[] directly, or wrap single imageUrl as fallback
      const img = sampleData.images && sampleData.images.length > 0
        ? sampleData.images
        : sampleData.imageUrl
          ? [{ imageUrl: sampleData.imageUrl, itemTitle: null, personalization: null }]
          : [];
      setImages(img);
      setCustomerEmail(sampleData.customerEmail || "");
      setTexts({ ...REVIEW_TEXT_DEFAULTS, ...(textConfig || {}) });
      setNotice((sampleData && sampleData.customerNotice) || "");
      return;
    }
    const fetchData = async () => {
      // Fetch draft + config in parallel
      const [draftSnap, configSnap] = await Promise.all([
        getDoc(doc(db, "drafts", draftId)),
        getDoc(doc(db, "config", "reviewPage")),
      ]);

      if (draftSnap.exists()) {
        const data = draftSnap.data();
        setCustomerEmail(data.customerEmail || "");
        setNotice(
          typeof data.customerNotice === "string" ? data.customerNotice : "");

        if (data.reviewedAt) {
          const currentUser = getAuth().currentUser;
          if (currentUser) {
            // Admin: show draft in read-only audit view
            setIsAdminView(true);
            setAdminDecision(data.customerDecision || null);
            setAdminFeedback(data.customerFeedback || null);
            setAdminReviewedAt(
              data.reviewedAt.toDate ? data.reviewedAt.toDate() : new Date(data.reviewedAt)
            );
            if (data.images && data.images.length > 0) {
              setImages(data.images);
            } else if (data.imageUrl) {
              setImages([{ imageUrl: data.imageUrl, itemTitle: null, personalization: null }]);
            }
          } else {
            setDraftExists(false);
          }
        } else if (data.images && data.images.length > 0) {
          setImages(data.images);
        } else if (data.imageUrl) {
          setImages([{ imageUrl: data.imageUrl, itemTitle: null,
            personalization: null }]);
        }
      } else {
        setDraftExists(false);
      }

      if (configSnap.exists()) {
        setTexts({ ...REVIEW_TEXT_DEFAULTS, ...configSnap.data() });
      }
    };
    fetchData();
  }, [draftId, previewMode, sampleData, textConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (previewMode) return;
    if (fehlenderAenderungstext(reviewDecision, feedback)) return;
    setIsSubmitting(true);
    try {
      await submitReviewFeedback({ draftId, customerEmail, reviewDecision, feedback });
      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting review:", error);
      alert("Etwas ist schiefgelaufen. Bitte versuche es später erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAdminView) {
    const dateStr = adminReviewedAt
      ? adminReviewedAt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "–";
    const decisionLabel = adminDecision === "approved" ? "Freigegeben" : adminDecision === "modification" ? "Änderung angefragt" : "–";
    const bannerColor = adminDecision === "approved" ? "#d4edda" : "#fff3cd";
    const bannerBorder = adminDecision === "approved" ? "#28a745" : "#f6ad55";
    return (
      <div style={{ minHeight: "100vh", background: C.pageBg, padding: "2.5em 1em 4em" }}>
        <div style={{ maxWidth: 600, margin: "0 auto", background: C.card, borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(74,44,42,0.10)", padding: "2.5em 2em" }}>
          <img src={SHOP.logo} alt={SHOP.name}
            style={{ height: 84, display: "block", margin: "0 auto 0.75em" }} />
          <div style={{ background: bannerColor, border: `1.5px solid ${bannerBorder}`,
            borderRadius: "8px", padding: "0.85em 1.1em", marginBottom: "1.75em", fontSize: "0.85rem" }}>
            <strong>Admin-Ansicht</strong> &mdash; Kunde hat <strong>{decisionLabel}</strong> am {dateStr}
            {adminFeedback && (
              <div style={{ marginTop: "0.5em", color: C.primaryDark }}>
                <strong>Kundenfeedback:</strong> {adminFeedback}
              </div>
            )}
          </div>
        {/* Hinweis aus dem Auto-Entwurf — steht bewusst ueber dem Bild,
            damit der Kunde ihn liest, bevor er den Entwurf beurteilt. */}
        {notice && (
          <div style={{
            background: "#fff8e1", border: "1.5px solid #e6a545",
            borderRadius: "8px", padding: "0.9em 1.1em",
            marginBottom: "1.75em", color: "#5c4408",
            fontSize: "0.9rem", lineHeight: 1.5,
          }}>
            <strong>Hinweis zu deinem Entwurf</strong>
            <div style={{ marginTop: "0.35em", whiteSpace: "pre-wrap" }}>
              {notice}
            </div>
          </div>
        )}

          {images.map((img, i) => (
            <div key={i} style={{ marginBottom: "2em" }}>
              {img.itemTitle && (
                <div style={{ fontSize: "0.78rem", fontWeight: "700", color: C.secondaryText,
                  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "0.4em" }}>
                  Artikel {i + 1}: {img.itemTitle}
                </div>
              )}
              <div onClick={() => setZoomed(i)} style={{ position: "relative", cursor: "zoom-in" }}>
                <img src={img.imageUrl} alt={img.itemTitle ? `Entwurf: ${img.itemTitle}` : "Entwurf"}
                  style={{ width: "100%", borderRadius: "10px", border: `1px solid ${C.border}`,
                    boxShadow: "0 2px 12px rgba(74,44,42,0.08)", display: "block" }} />
                <div style={{ position: "absolute", bottom: "0.6em", right: "0.6em",
                  background: "rgba(255,255,255,0.85)", borderRadius: "50%",
                  width: "2em", height: "2em", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: "1rem",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)", pointerEvents: "none" }}>
                  🔍
                </div>
              </div>
            </div>
          ))}
        </div>
        {zoomed !== null && images[zoomed] && (
          <div onClick={() => setZoomed(null)} style={{ position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center",
            justifyContent: "center", zIndex: 2000, cursor: "zoom-out", padding: "1.5em" }}>
            <img src={images[zoomed].imageUrl} alt="Entwurf (Vollbild)"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: "8px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.5)", cursor: "default" }} />
            <button onClick={() => setZoomed(null)} style={{ position: "absolute",
              top: "1rem", right: "1rem", background: "rgba(255,255,255,0.15)",
              border: "none", color: "#fff", fontSize: "1.5rem", width: "2.4rem",
              height: "2.4rem", borderRadius: "50%", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
              aria-label="Schließen">×</button>
          </div>
        )}
      </div>
    );
  }

  if (!draftExists) {
    return (
      <div style={{ minHeight: "100vh", background: C.pageBg,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "2em" }}>
        <div style={{ textAlign: "center" }}>
          <img src={SHOP.logo} alt={SHOP.name} style={{ height: 52, marginBottom: "1.5em" }} />
          <h2 style={{ color: C.primaryDark }}>Entwurf nicht verfügbar</h2>
          <p style={{ color: C.secondaryText }}>
            Dieser Entwurf ist nicht mehr verfügbar oder wurde bereits freigegeben.
          </p>
        </div>
      </div>
    );
  }

  const isApproved     = reviewDecision === "approved";
  const isModification = reviewDecision === "modification";
  const fehlenderText = fehlenderAenderungstext(reviewDecision, feedback);

  return (
    <div style={{ minHeight: "100vh", background: C.pageBg, padding: "2.5em 1em 4em" }}>

      <div style={{
        maxWidth: 600, margin: "0 auto",
        background: C.card, borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(74,44,42,0.10)",
        padding: "2.5em 2em",
      }}>

        {/* Logo */}
        <img
          src={SHOP.logo}
          alt={SHOP.name}
          style={{ height: 84, display: "block", margin: "0 auto 0.75em" }}
        />

        {/* Page title */}
        <div style={{
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: "0.22em",
          color: C.secondaryText,
          fontSize: "0.78rem",
          fontWeight: "600",
          marginBottom: "1.75em",
        }}>
          {texts.pageTitle}
        </div>


        {/* Hinweis aus dem Auto-Entwurf — steht bewusst ueber dem Bild,
            damit der Kunde ihn liest, bevor er den Entwurf beurteilt. */}
        {notice && (
          <div style={{
            background: "#fff8e1", border: "1.5px solid #e6a545",
            borderRadius: "8px", padding: "0.9em 1.1em",
            marginBottom: "1.75em", color: "#5c4408",
            fontSize: "0.9rem", lineHeight: 1.5,
          }}>
            <strong>Hinweis zu deinem Entwurf</strong>
            <div style={{ marginTop: "0.35em", whiteSpace: "pre-wrap" }}>
              {notice}
            </div>
          </div>
        )}

        {/* Draft images */}
        {images.length > 0 ? images.map((img, i) => (
          <div key={i} style={{ marginBottom: "2em" }}>
            {/* Item label — only shown for multi-image or named items */}
            {img.itemTitle && (
              <div style={{
                fontSize: "0.78rem", fontWeight: "700",
                color: C.secondaryText, textTransform: "uppercase",
                letterSpacing: "0.08em", marginBottom: "0.4em",
              }}>
                Artikel {i + 1}: {img.itemTitle}
              </div>
            )}
            <div
              onClick={() => setZoomed(i)}
              style={{ position: "relative", cursor: "zoom-in" }}
            >
              <img
                src={img.imageUrl}
                alt={img.itemTitle ? `Entwurf: ${img.itemTitle}` : "Entwurf"}
                style={{
                  width: "100%", borderRadius: "10px",
                  border: `1px solid ${C.border}`,
                  boxShadow: "0 2px 12px rgba(74,44,42,0.08)",
                  display: "block",
                }}
              />
              <div style={{
                position: "absolute", bottom: "0.6em", right: "0.6em",
                background: "rgba(255,255,255,0.85)",
                borderRadius: "50%", width: "2em", height: "2em",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                pointerEvents: "none",
              }}>
                🔍
              </div>
            </div>
          </div>
        )) : (
          <div style={{
            width: "100%", height: "220px", borderRadius: "10px",
            border: `1px solid ${C.border}`, background: "#f9f0eb",
            display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: "2em", color: C.secondaryText, fontStyle: "italic",
            fontSize: "0.9rem",
          }}>
            Entwurf wird geladen…
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>

          {/* Approve option */}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: "0.75em",
            padding: "1.1em 1.1em",
            border: `1.5px solid ${isApproved ? C.selectedBorder : C.border}`,
            borderRadius: "10px", cursor: "pointer",
            background: isApproved ? C.selectedBg : C.card,
            marginBottom: "0.75em",
            transition: "border-color 0.15s, background 0.15s",
          }}>
            <input
              type="radio"
              name="reviewDecision"
              value="approved"
              checked={isApproved}
              onChange={(e) => setReviewDecision(e.target.value)}
              style={{ marginTop: "3px", accentColor: C.primaryDark, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: "600", color: C.primaryDark, fontSize: "0.9rem",
                lineHeight: "1.2", marginBottom: "0.65em" }}>
                {texts.approveMainText}
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.2em",
                color: C.bulletText, fontSize: "0.8rem", lineHeight: "1.15" }}>
                {(texts.approveBullets || []).map((b, i) => (
                  <li key={i} style={{ marginBottom: "0.35em" }}>{b}</li>
                ))}
              </ul>
            </div>
          </label>

          {/* Modification option */}
          <label style={{
            display: "flex", alignItems: "flex-start", gap: "0.75em",
            padding: "1.1em 1.1em",
            border: `1.5px solid ${isModification ? C.selectedBorder : C.border}`,
            borderRadius: "10px", cursor: "pointer",
            background: isModification ? C.selectedBg : C.card,
            marginBottom: "1.25em",
            transition: "border-color 0.15s, background 0.15s",
          }}>
            <input
              type="radio"
              name="reviewDecision"
              value="modification"
              checked={isModification}
              onChange={(e) => setReviewDecision(e.target.value)}
              style={{ marginTop: "3px", accentColor: C.primaryDark, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: "600", color: C.primaryDark,
                fontSize: "0.9rem", lineHeight: "1.2", marginBottom: "0.4em" }}>
                {texts.modificationLabel}
              </div>
              <div style={{ color: C.bulletText, fontSize: "0.8rem", lineHeight: "1.2" }}>
                {texts.modificationSubtext}
              </div>
            </div>
          </label>

          {/* Feedback textarea — visible only when modification selected */}
          {isModification && (
            <div style={{ marginBottom: "1.25em" }}>
              <label style={{
                display: "block", fontWeight: "600", fontSize: "0.85rem",
                color: C.primaryDark, marginBottom: "0.5em",
              }}>
                {texts.feedbackLabel}
              </label>
              <textarea
                placeholder={texts.feedbackPlaceholder}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows="5"
                style={{
                  width: "100%", padding: "0.75em",
                  border: `1.5px solid ${C.border}`, borderRadius: "8px",
                  fontSize: "0.875rem", boxSizing: "border-box", resize: "vertical",
                  color: C.primaryDark, background: SHOP.pageBg,
                  fontFamily: "inherit",
                }}
              />
              {fehlenderText && (
                <p style={{
                  margin: "0.5em 0 0", fontSize: "0.8rem",
                  color: C.secondaryText, lineHeight: 1.45,
                }}>
                  {texts.feedbackRequiredHint}
                </p>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting || previewMode || fehlenderText}
            style={{
              width: "100%", padding: "0.8em",
              background: (isSubmitting || previewMode || fehlenderText)
                ? SHOP.accent : C.primaryDark,
              color: "#fff", border: "none", borderRadius: "8px",
              fontSize: "1rem", fontWeight: "700",
              letterSpacing: "0.05em",
              cursor: (isSubmitting || previewMode || fehlenderText)
                ? "not-allowed" : "pointer",
              transition: "background 0.15s",
            }}
          >
            {isSubmitting ? "Wird gesendet…" : texts.submitButtonText}
          </button>

        </form>
      </div>

      {/* Lightbox overlay */}
      {zoomed !== null && images[zoomed] && (
        <div
          onClick={() => setZoomed(null)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 2000, cursor: "zoom-out", padding: "1.5em",
          }}
        >
          <img
            src={images[zoomed].imageUrl}
            alt="Entwurf (Vollbild)"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "100%", maxHeight: "90vh",
              borderRadius: "8px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
              cursor: "default",
            }}
          />
          <button
            onClick={() => setZoomed(null)}
            style={{
              position: "absolute", top: "1rem", right: "1rem",
              background: "rgba(255,255,255,0.15)", border: "none",
              color: "#fff", fontSize: "1.5rem",
              width: "2.4rem", height: "2.4rem",
              borderRadius: "50%", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              lineHeight: 1,
            }}
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default DraftReviewForm;
